import { useState, type FormEvent } from "react";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { ResendVerification } from "@/components/resend-verification";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { createErrorMessage } from "@/lib/error-message";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LandingNavbar } from "@/components/landing-navbar";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  BarChart3,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Monitor,
  Smartphone,
} from "lucide-react";

export const Route = createFileRoute("/")({
  beforeLoad: ({ context }) => {
    if (context.auth.loading) return;
    if (context.auth.isAuthenticated) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: LandingPage,
});

function LandingPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const features = [
    {
      icon: BookOpen,
      title: t("landing.feature_practice_title"),
      description: t("landing.feature_practice_desc"),
      badge: t("landing.feature_practice_badge"),
    },
    {
      icon: BarChart3,
      title: t("landing.feature_analysis_title"),
      description: t("landing.feature_analysis_desc"),
      badge: t("landing.feature_analysis_badge"),
    },
    {
      icon: TrendingUp,
      title: t("landing.feature_progress_title"),
      description: t("landing.feature_progress_desc"),
      badge: t("landing.feature_progress_badge"),
    },
  ];

  const highlights = [
    t("landing.highlight_1"),
    t("landing.highlight_2"),
    t("landing.highlight_3"),
    t("landing.highlight_4"),
  ];

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError("");

    setIsSubmitting(true);
    try {
      await login({ email, password });
      navigate({ to: "/dashboard" });
    } catch (err) {
      setError(createErrorMessage(err, t("auth.login_error")));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-700">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-purple-400/10 blur-3xl" />
          <div className="absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-400/5 blur-3xl" />
        </div>

        <LandingNavbar />

        <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-4 py-16 sm:px-6 sm:py-20 md:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:py-32">
          {/* Hero copy */}
          <div className="text-center lg:text-left">
            <Badge
              variant="secondary"
              className="mb-6 bg-white/15 text-white backdrop-blur-sm"
            >
              <Sparkles className="mr-1 size-3" />
              {t("landing.hero_badge")}
            </Badge>

            <h1 className="text-3xl font-bold tracking-tight whitespace-pre-line text-white sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
              {t("landing.hero_title")}
            </h1>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-blue-100 sm:text-lg lg:mx-0 lg:max-w-none">
              {t("landing.hero_desc")}
            </p>
            <p className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm text-white/90 backdrop-blur-sm lg:justify-start">
              <Monitor className="size-4" />
              <span>+</span>
              <Smartphone className="size-4" />
              <span className="ml-1">{t("landing.hero_sub")}</span>
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:gap-4 lg:items-start">
              <Link to="/register" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full cursor-pointer bg-white px-8 font-semibold text-blue-700 hover:bg-blue-50 sm:w-auto"
                >
                  {t("landing.get_started")}
                  <ArrowRight className="ml-1 size-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Inline login card */}
          <div className="mx-auto w-full max-w-md lg:mx-0">
            <Card className="border-white/20 bg-white/95 shadow-2xl backdrop-blur dark:bg-card/95">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">
                  {t("auth.login_title")}
                </CardTitle>
                <CardDescription>
                  {t("auth.login_desc")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="flex flex-col gap-4">
                  {error && (
                    <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                      {error}
                    </div>
                  )}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="hero-email">{t("auth.email")}</Label>
                    <Input
                      id="hero-email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="hero-password">{t("auth.password")}</Label>
                    <Input
                      id="hero-password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      disabled={isSubmitting}
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="cursor-pointer"
                  >
                    {isSubmitting
                      ? t("auth.login_loading")
                      : t("auth.login_title")}
                  </Button>
                  <div className="text-center text-sm">
                    <Link
                      to="/forgot-password"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      비밀번호를 잊으셨나요?
                    </Link>
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    {t("auth.no_account")}{" "}
                    <Link
                      to="/register"
                      className="font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {t("nav.register")}
                    </Link>
                  </p>
                </form>
          <ResendVerification email={email} />
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="bg-muted/30 py-16 sm:py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl">
              {t("landing.cta_title")}
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
              {t("landing.cta_desc")}
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:mt-12 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className="transition-shadow hover:shadow-lg"
              >
                <CardHeader>
                  <div className="mb-2 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-400">
                      <feature.icon className="size-5" />
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {feature.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                  <CardDescription className="leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-16 sm:py-20 md:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16">
            <div>
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {t("landing.highlights_title")}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {t("landing.highlights_desc")}
              </p>
              <ul className="mt-6 space-y-3">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-purple-50 dark:border-blue-900 dark:from-blue-950/50 dark:to-purple-950/50">
              <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
                <div className="flex items-center gap-3 text-blue-600 dark:text-blue-300">
                  <Monitor className="size-12" />
                  <span className="text-2xl font-bold">+</span>
                  <Smartphone className="size-10" />
                </div>
                <h3 className="text-xl font-semibold">
                  {t("landing.cta_start_title")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("landing.cta_start_desc")}
                  <br />
                  {t("landing.cta_start_sub")}
                </p>
                <div className="mt-2 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                  <Link to="/register" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      className="w-full cursor-pointer px-8 sm:w-auto"
                    >
                      {t("landing.start_free")}
                      <ArrowRight className="ml-1 size-4" />
                    </Button>
                  </Link>
                  <Link to="/login" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full cursor-pointer px-8 sm:w-auto"
                    >
                      {t("nav.login")}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-8">
        <div className="mx-auto max-w-6xl px-4 text-center text-sm text-muted-foreground sm:px-6">
          <p>{t("landing.footer")}</p>
        </div>
      </footer>
    </div>
  );
}
